import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeInUp, SectionLabel, AccentLine } from './AnimationWrappers';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['All', 'Technical Outerwear', 'Workwear', 'Corporate Wear', 'Activewear & Performance', 'Sustainable Childrenswear', 'High Fashion'];

const complexityToWidth = { 'Low': 20, 'Medium': 40, 'Moderate': 50, 'High': 65, 'Very High': 82, 'Highest': 100 };

const HeroProductsSection = () => {
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('Technical Outerwear');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch (e) { console.error('Failed to fetch products', e); }
    finally { setLoading(false); }
  };

  const filtered = activeCategory === 'All' ? products : products.filter(p => p.category === activeCategory);
  const currentProduct = filtered[currentIndex];
  const total = filtered.length;

  const goNext = () => setCurrentIndex((prev) => (prev + 1) % total);
  const goPrev = () => setCurrentIndex((prev) => (prev - 1 + total) % total);

  useEffect(() => { setCurrentIndex(0); }, [activeCategory]);

  // Truncate description for display
  const truncateDesc = (text, maxLen = 180) => {
    if (!text || text.length <= maxLen) return text;
    return text.substring(0, maxLen).replace(/\s+\S*$/, '') + '...';
  };

  // Show limited specs
  const getVisibleSpecs = (specs, max = 4) => {
    if (!specs) return [];
    return Object.entries(specs).slice(0, max);
  };

  if (loading) return null;

  return (
    <section id="products" data-testid="products-section" className="relative overflow-hidden" style={{ background: '#F5F0EB', padding: '70px 0 50px' }}>
      <div className="container-main">
        <SectionLabel number="05" title="Hero Products" />
        <FadeInUp>
          <AccentLine />
          <h2 className="font-display mb-3" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 700, color: '#1C1C1E' }}>
            Engineering Under the <span style={{ color: '#C8102E' }}>Seams.</span>
          </h2>
          <p className="font-body mb-10" style={{ fontSize: '1.1rem', color: '#6B6B6F', maxWidth: '580px' }}>
            Every garment is an engineering problem solved. Browse by category — from military-spec outerwear to laser-cut activewear.
          </p>
        </FadeInUp>

        {/* Category tabs */}
        <FadeInUp delay={0.1}>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-10" style={{ maxWidth: '900px' }}>
            {CATEGORIES.filter(c => c !== 'All').map((cat) => {
              const count = products.filter(p => p.category === cat).length;
              const isActive = activeCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  data-testid={`product-category-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                  className="flex items-center justify-between px-5 py-4 rounded-lg transition-all duration-300"
                  style={{
                    background: isActive ? '#1C1C1E' : '#FFFFFF',
                    color: isActive ? '#FFFFFF' : '#1C1C1E',
                    border: `1px solid ${isActive ? '#1C1C1E' : '#D4CFC7'}`,
                  }}
                >
                  <span className="font-body text-sm font-medium text-left">{cat}</span>
                  <div className="flex items-center gap-2">
                    {count > 0 && <span className="font-mono text-xs" style={{ color: isActive ? 'rgba(255,255,255,0.5)' : '#C8102E' }}>{count}</span>}
                    <ArrowRight size={14} style={{ color: isActive ? '#C8102E' : '#D4CFC7' }} />
                  </div>
                </button>
              );
            })}
          </div>
        </FadeInUp>

        {/* Product Carousel — Compact Layout */}
        {currentProduct && (
          <FadeInUp delay={0.2}>
            <div className="relative card-light overflow-hidden" data-testid="product-carousel">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentProduct.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                >
                  {/* === DESKTOP LAYOUT === */}
                  <div className="hidden lg:grid lg:grid-cols-[42%_58%]" style={{ maxHeight: '420px' }}>
                    {/* Content — Left */}
                    <div className="p-7 flex flex-col justify-between overflow-hidden">
                      <div>
                        <span className="font-mono block mb-2" style={{ fontSize: '0.58rem', color: '#C8102E', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                          {currentProduct.category} / {currentProduct.subcategory}
                        </span>
                        <h3 className="font-display mb-3" style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1C1C1E', lineHeight: 1.2 }}>
                          {currentProduct.name}
                        </h3>
                        <p className="font-body mb-4" style={{ fontSize: '0.78rem', color: '#3A3A3C', lineHeight: 1.65 }}>
                          {truncateDesc(currentProduct.description, 200)}
                        </p>

                        {/* Compact specs — 2x2 grid, first 4 only */}
                        {currentProduct.specs && (
                          <div className="grid grid-cols-2 gap-px mb-4 rounded-lg overflow-hidden" style={{ background: '#D4CFC7' }} data-testid="product-specs-grid">
                            {getVisibleSpecs(currentProduct.specs, 4).map(([key, val], i) => (
                              <div key={i} style={{ background: '#FAF8F5', padding: '8px 10px' }}>
                                <span className="font-mono block" style={{ fontSize: '0.48rem', color: '#6B6B6F', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '2px' }}>{key}</span>
                                <span className="font-body" style={{ fontSize: '0.7rem', color: '#1C1C1E', fontWeight: 500, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{val}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Bottom: complexity + tags inline */}
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 flex-1" data-testid="product-complexity">
                          <span className="font-mono" style={{ fontSize: '0.55rem', color: '#6B6B6F', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Complexity</span>
                          <div className="flex-1 max-w-[100px] h-1.5 rounded-full overflow-hidden" style={{ background: '#E8E0D5' }}>
                            <motion.div
                              className="h-full rounded-full"
                              style={{ background: '#C8102E' }}
                              initial={{ width: 0 }}
                              animate={{ width: `${complexityToWidth[currentProduct.complexity] || 50}%` }}
                              transition={{ duration: 0.8 }}
                            />
                          </div>
                          <span className="font-mono" style={{ fontSize: '0.58rem', color: '#C8102E', fontWeight: 600 }}>{currentProduct.complexity}</span>
                        </div>
                        {currentProduct.tags?.slice(0, 2).map((tag, i) => (
                          <span key={i} className="font-mono" style={{
                            fontSize: '0.48rem', letterSpacing: '0.06em', textTransform: 'uppercase',
                            padding: '3px 8px', borderRadius: '3px',
                            background: '#C8102E', color: '#FFFFFF',
                          }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Image — Right */}
                    <div className="relative flex items-center justify-center overflow-hidden" style={{ background: '#F0EBE4' }}>
                      <img
                        src={currentProduct.image_url}
                        alt={currentProduct.name}
                        className="w-full h-full object-contain"
                        style={{ maxHeight: '420px', padding: '12px' }}
                        data-testid="product-image"
                      />
                      <span className="absolute bottom-3 right-4 font-display leading-none" style={{ fontSize: '5rem', fontWeight: 300, color: 'rgba(28,28,30,0.06)' }}>
                        {String(currentIndex + 1).padStart(2, '0')}
                      </span>
                    </div>
                  </div>

                  {/* === MOBILE LAYOUT === */}
                  <div className="lg:hidden">
                    {/* Image — capped height */}
                    <div className="relative overflow-hidden flex items-center justify-center" style={{ height: '240px', background: '#F0EBE4' }}>
                      <img
                        src={currentProduct.image_url}
                        alt={currentProduct.name}
                        className="max-w-full max-h-full object-contain"
                        style={{ padding: '8px' }}
                        data-testid="product-image-mobile"
                      />
                      {currentProduct.tags?.[0] && (
                        <span className="absolute top-3 right-3 font-mono text-white px-2.5 py-1 rounded" style={{ background: '#C8102E', fontSize: '0.48rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                          {currentProduct.tags[0]}
                        </span>
                      )}
                    </div>

                    {/* Content — compact */}
                    <div className="p-5">
                      <span className="font-mono block mb-1.5" style={{ fontSize: '0.55rem', color: '#C8102E', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                        {currentProduct.category}
                      </span>
                      <h3 className="font-display mb-2" style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1C1C1E', lineHeight: 1.2 }}>
                        {currentProduct.name}
                      </h3>
                      <p className="font-body mb-3" style={{
                        fontSize: '0.76rem', color: '#3A3A3C', lineHeight: 1.6,
                        display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {currentProduct.description}
                      </p>

                      {/* Mobile specs — 2 only */}
                      {currentProduct.specs && (
                        <div className="grid grid-cols-2 gap-px mb-3 rounded-lg overflow-hidden" style={{ background: '#D4CFC7' }}>
                          {getVisibleSpecs(currentProduct.specs, 2).map(([key, val], i) => (
                            <div key={i} style={{ background: '#FAF8F5', padding: '7px 10px' }}>
                              <span className="font-mono block" style={{ fontSize: '0.45rem', color: '#6B6B6F', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '2px' }}>{key}</span>
                              <span className="font-body" style={{ fontSize: '0.68rem', color: '#1C1C1E', fontWeight: 500, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{val}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Complexity bar — compact */}
                      <div className="flex items-center gap-2" data-testid="product-complexity-mobile">
                        <span className="font-mono" style={{ fontSize: '0.5rem', color: '#6B6B6F', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Complexity</span>
                        <div className="flex-1 max-w-[80px] h-1.5 rounded-full overflow-hidden" style={{ background: '#E8E0D5' }}>
                          <motion.div className="h-full rounded-full" style={{ background: '#C8102E' }} initial={{ width: 0 }} animate={{ width: `${complexityToWidth[currentProduct.complexity] || 50}%` }} transition={{ duration: 0.8 }} />
                        </div>
                        <span className="font-mono" style={{ fontSize: '0.55rem', color: '#C8102E', fontWeight: 600 }}>{currentProduct.complexity}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation Bar — Below card, always visible */}
            {total > 1 && (
              <div className="flex items-center justify-between mt-4 px-1" data-testid="product-pagination">
                <button
                  onClick={goPrev}
                  data-testid="product-prev"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all duration-200 hover:bg-white"
                  style={{ border: '1px solid #D4CFC7', background: 'rgba(255,255,255,0.6)' }}
                >
                  <ChevronLeft size={16} color="#1C1C1E" />
                  <span className="font-mono hidden sm:inline" style={{ fontSize: '0.6rem', color: '#6B6B6F', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Prev</span>
                </button>

                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    {filtered.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentIndex(i)}
                        className="rounded-full transition-all duration-300"
                        style={{
                          width: i === currentIndex ? '24px' : '8px',
                          height: '8px',
                          background: i === currentIndex ? '#C8102E' : '#D4CFC7',
                        }}
                      />
                    ))}
                  </div>
                  <span className="font-mono" style={{ fontSize: '0.72rem', color: '#6B6B6F' }}>
                    <span style={{ color: '#C8102E', fontWeight: 600 }}>{String(currentIndex + 1).padStart(2, '0')}</span>
                    {' / '}{String(total).padStart(2, '0')}
                  </span>
                </div>

                <button
                  onClick={goNext}
                  data-testid="product-next"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all duration-200 hover:bg-white"
                  style={{ border: '1px solid #D4CFC7', background: 'rgba(255,255,255,0.6)' }}
                >
                  <span className="font-mono hidden sm:inline" style={{ fontSize: '0.6rem', color: '#6B6B6F', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Next</span>
                  <ChevronRight size={16} color="#1C1C1E" />
                </button>
              </div>
            )}
          </FadeInUp>
        )}
      </div>
      <div className="section-number hidden lg:block">05</div>
    </section>
  );
};

export default HeroProductsSection;
